import React from 'react'
import { Check, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export type ProcessingStepStatus = 'pending' | 'completed' | 'error'

interface ProcessingStep {
  label: string
  status: ProcessingStepStatus
}

interface ProcessingModalProps {
  open: boolean
  steps: ProcessingStep[]
}

export const ProcessingModal: React.FC<ProcessingModalProps> = ({ open, steps }) => {
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md [&>button]:hidden" 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-gray-800">Processing</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center justify-center py-6 space-y-6">
          {/* Loading Spinner */}
          <div className="relative">
            <Loader2 className="h-16 w-16 animate-spin text-purple-600" />
          </div>

          {/* Processing Steps */}
          <div className="w-full space-y-4">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3">
                  {step.status === 'completed' ? (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  ) : step.status === 'error' ? (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-300">
                      <Loader2 className="h-4 w-4 text-gray-600 animate-spin" />
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-700">{step.label}</span>
                </div>
                <Badge
                  variant={step.status === 'completed' ? 'default' : 'secondary'}
                  className={cn(
                    step.status === 'completed' && 'bg-teal-500 text-white border-teal-500',
                    step.status === 'error' && 'bg-red-500 text-white border-red-500',
                    step.status === 'pending' && 'bg-gray-300 text-gray-700 border-gray-300'
                  )}
                >
                  {step.status === 'completed' ? 'Completed' : step.status === 'error' ? 'Error' : 'Pending'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

